// RF-SLD20180014-0002 - Reconciliação mensal EDP
// Período de 26 do mês anterior a 25 do mês; três fases (Click, EDP, ficheiro final);
// discrepâncias por fase, exportações, rascunho dos e-mails e fatura agregada.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Breadcrumbs, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, Link, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import Grid from '@mui/system/Unstable_Grid';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import moment from 'moment';
import { PaginationFilterType } from '@types';
import { DynamicCard, Table } from '@components';
import { NOME_FASE } from './colunas';
import { DISCREPANCIA_LABEL, ErroReconciliacao, descarregarXlsx, exportarFase1, lerFicheiro, mensagemErro, textoEmail } from './ficheiros';
const money = (value) => (value ?? 0).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
const DISCREPANCIA_COR = {
    falta_click: 'error',
    falta_edp: 'error',
    tipo: 'warning',
    valor: 'info',
    repetido: 'warning',
    cobrado_local: 'error'
};
const ESTADO_PEDIDO = {
    1: 'Aguarda processamento',
    2: 'Em processamento',
    3: 'Erro',
    4: 'Concluído'
};
/**
 * Filtra e ordena linhas em memória, com os filtros e a ordenação do `Table` partilhado.
 *
 * @param {T[]} rows Linhas
 * @param {FilterProperties[]} filter Filtros do `Table` (texto contido, sem maiúsculas)
 * @param {SortProperties[]} sort Ordenação do `Table` (só a primeira coluna)
 * @param {(r: T, id: string) => any} valor Valor de uma coluna para filtrar e ordenar
 * @returns {T[]} Linhas filtradas e ordenadas
 */
const filtrarOrdenar = (rows, filter, sort, valor) => {
    let out = [...rows];
    filter.forEach(f => {
        const val = String(f.value ?? '').toLowerCase().trim();
        if (!val)
            return;
        out = out.filter(r => {
            const cell = valor(r, f.id);
            return cell != null && String(cell).toLowerCase().includes(val);
        });
    });
    if (sort.length) {
        const s = sort[0];
        out.sort((a, b) => {
            const av = valor(a, s.id);
            const bv = valor(b, s.id);
            const c = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av ?? '').localeCompare(String(bv ?? ''));
            return s.desc ? -c : c;
        });
    }
    return out;
};
/**
 * Página de linhas no formato que o `Table` partilhado espera.
 *
 * @param {T[]} rows Linhas já filtradas e ordenadas
 * @param {MRT_PaginationState} pagination Página e tamanho
 * @returns {PaginatedDocumented<T>} Página
 */
const paginar = (rows, pagination) => {
    const pageSize = pagination.pageSize || 20;
    const start = pagination.pageIndex * pageSize;
    return {
        data: rows.slice(start, start + pageSize),
        meta: { currentPage: pagination.pageIndex + 1, itemsPerPage: pageSize, totalItems: rows.length, totalPages: Math.max(1, Math.ceil(rows.length / pageSize)) },
        links: {}
    };
};
/**
 * Período de um mês. O mês do período é o do dia 25 (fim).
 * Ex.: «2026-09» = 26/08/2026 a 25/09/2026.
 *
 * @param {moment.Moment} mes Mês do período
 * @returns {{from: string, to: string}} Datas YYYY-MM-DD
 */
const periodoDoMes = (mes) => ({
    from: mes.clone().subtract(1, 'month').date(26).format('YYYY-MM-DD'),
    to: mes.clone().date(25).format('YYYY-MM-DD')
});
/**
 * Mês a mostrar ao abrir: o do URL (`?mes=AAAA-MM`) ou o último período fechado.
 *
 * @param {string} param Valor de `$state.params.mes`
 * @returns {moment.Moment} Primeiro dia do mês
 */
const mesInicial = (param) => {
    const m = param ? moment(param, 'YYYY-MM', true) : null;
    if (m && m.isValid())
        return m.startOf('month');
    // Por omissão, o último período fechado: até ao dia 25 é o do mês anterior
    const hoje = moment();
    return (hoje.date() > 25 ? hoje : hoje.subtract(1, 'month')).startOf('month');
};
const GasReconciliacaoEdpView = ({ $state, GasService, UIService }) => {
    const [mes, setMes] = useState(() => mesInicial($state.params.mes));
    const { from, to } = useMemo(() => periodoDoMes(mes), [mes]);
    const [loading, setLoading] = useState(false);
    const [rec, setRec] = useState(null);
    const [faseVista, setFaseVista] = useState(1);
    const [soDiscrepancias, setSoDiscrepancias] = useState(true);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
    const [sort, setSort] = useState([]);
    const [filter, setFilter] = useState([]);
    const [selecionados, setSelecionados] = useState(new Set());
    const [confirmar, setConfirmar] = useState(false);
    const [emitindo, setEmitindo] = useState(false);
    const [email, setEmail] = useState(null);
    const [config, setConfig] = useState(null);
    const [aviso, setAviso] = useState(null);
    /**
     * Mostra uma mensagem no topo da vista e também como toast. O toast é do Angular e,
     * chamado a partir de código assíncrono do React (ex.: leitura do ficheiro), pode
     * não chegar a aparecer; o aviso na vista aparece sempre.
     *
     * @param {'success'|'error'} tipo Tipo de mensagem
     * @param {string} texto Mensagem
     */
    const avisar = (tipo, texto) => {
        setAviso({ tipo, texto });
        UIService.addToast(texto);
    };
    const inputs = { 1: useRef(null), 2: useRef(null), 3: useRef(null) };
    /**
     * Mostra um cruzamento novo e marca, na fase 3, todos os processos faturáveis.
     *
     * @param {IGasReconciliacaoEdp} r Cruzamento devolvido pelo servidor
     */
    const aplicar = (r) => {
        setRec(r);
        // Fase 3: todos os processos faturáveis vêm marcados; o RT desmarca o que não quer faturar
        setSelecionados(new Set(r.processos.filter(p => p.faturavel).map(p => p.id)));
        if (r.cargas[2])
            setFaseVista(2);
    };
    // Último período pedido: uma resposta que chegue depois de se mudar de mês é ignorada
    const periodoPedido = useRef('');
    /**
     * Carrega o cruzamento do período escolhido e atualiza o `?mes=` do URL.
     */
    const fetchData = async () => {
        const pedido = `${from}/${to}`;
        periodoPedido.current = pedido;
        setLoading(true);
        try {
            const r = await GasService.getReconciliacaoEdp(from, to);
            if (periodoPedido.current !== pedido)
                return;
            aplicar(r);
            $state.go($state.current.name, { mes: mes.format('YYYY-MM') }, { notify: false, reload: false, location: 'replace', inherit: true });
        }
        catch (e) {
            if (periodoPedido.current !== pedido)
                return;
            avisar('error', mensagemErro(e, 'Ocorreu um erro ao carregar a reconciliação.'));
        }
        setLoading(false);
    };
    useEffect(() => {
        setPagination(p => ({ ...p, pageIndex: 0 }));
        setFaseVista(1);
        fetchData();
    }, [from, to]);
    /**
     * Lê o ficheiro escolhido, envia as linhas ao servidor e mostra o cruzamento atualizado.
     *
     * @param {GasEdpFase} fase Fase do ficheiro
     * @param {File} file Ficheiro escolhido no input (nada acontece se vier vazio)
     */
    const carregar = async (fase, file) => {
        if (!file)
            return;
        setLoading(true);
        try {
            const linhas = await lerFicheiro(file, fase);
            if (linhas.length === 0)
                throw new ErroReconciliacao('O ficheiro não tem linhas com nº de processo.');
            aplicar(await GasService.carregarFicheiroEdp({ fase, from, to, ficheiroNome: file.name, linhas }));
            avisar('success', `Ficheiro ${NOME_FASE[fase]} carregado: ${linhas.length} linhas.`);
        }
        catch (e) {
            avisar('error', mensagemErro(e, 'Ocorreu um erro ao carregar o ficheiro.'));
        }
        setLoading(false);
        const input = inputs[fase].current;
        if (input)
            input.value = '';
    };
    /**
     * Exportação da fase: na 1, a lista das discrepâncias (gerada no browser); na 2, o
     * ficheiro da EDP corrigido, gerado no servidor com as linhas corrigidas pintadas.
     *
     * @param {1|2} fase Fase a exportar
     */
    const exportar = async (fase) => {
        if (!rec)
            return;
        if (fase === 1) {
            exportarFase1(rec);
            return;
        }
        try {
            descarregarXlsx(await GasService.exportarEdpCorrigido(from, to), rec, 'fase2-edp-corrigido');
        }
        catch (e) {
            avisar('error', mensagemErro(e, 'Ocorreu um erro ao exportar o ficheiro da EDP.'));
        }
    };
    /**
     * Abre o diálogo com o rascunho do e-mail da fase. O anexo descarrega-se no próprio
     * diálogo («Descarregar anexo») ou pelo «Exportar» da tabela.
     *
     * @param {1|2} fase Fase do e-mail
     */
    const prepararEmail = (fase) => {
        if (!rec)
            return;
        setEmail({ fase, ...textoEmail(rec, fase), para: '' });
    };
    /**
     * Abre a confirmação da emissão com o cliente e o nº de compromisso da parametrização.
     */
    const abrirConfirmacao = async () => {
        setConfirmar(true);
        try {
            setConfig(await GasService.getConfig());
        }
        catch (e) {
            setConfig(null);
            avisar('error', mensagemErro(e, 'Não foi possível ler a parametrização da faturação EDP.'));
        }
    };
    /**
     * Emite a fatura agregada com os processos marcados e recarrega o período.
     */
    const emitir = async () => {
        setEmitindo(true);
        try {
            const r = await GasService.emitirFaturaEdp(from, to, Array.from(selecionados));
            avisar('success', `Pedido de fatura criado: ${r.count} processos, ${money(r.total)}.`);
            setConfirmar(false);
            await fetchData();
        }
        catch (e) {
            avisar('error', mensagemErro(e, 'Ocorreu um erro ao emitir a fatura.'));
        }
        setEmitindo(false);
    };
    // ── Tabela de discrepâncias ───────────────────────────────────────────────
    const discrepanciasDe = (p) => (faseVista === 1 ? p.discrepancias.fase1 : p.discrepancias.fase2);
    const columns = useMemo(() => {
        const fonte = faseVista === 1 ? 'click' : 'edp';
        const nome = faseVista === 1 ? 'Click' : 'EDP';
        const money0 = (v) => (v === null || v === undefined ? '—' : money(v));
        return [
            { accessorKey: 'numeroProcesso', header: 'Nº Processo', size: 110 },
            { accessorKey: 'numeroParceiro', header: 'Nº Parceiro', size: 120 },
            { accessorKey: 'data', header: 'Data Inspeção', size: 110, Cell: ({ cell }) => (cell.getValue() ? moment(cell.getValue()).format('DD/MM/YYYY') : '—') },
            { accessorKey: 'valorSemIva', header: 'Valor IEP s/IVA', Cell: ({ cell }) => money0(cell.getValue()) },
            { accessorKey: `valor${nome}`, accessorFn: (r) => r[fonte]?.valor ?? null, header: `Valor ${nome}`, Cell: ({ cell }) => money0(cell.getValue()) },
            { accessorKey: 'tipoInstalacao', header: 'Tipo IEP', size: 110 },
            { accessorKey: `tipo${nome}`, accessorFn: (r) => r[fonte]?.tipoInstalacao ?? '', header: `Tipo ${nome}`, size: 110 },
            {
                accessorKey: 'discrepancias',
                accessorFn: (r) => discrepanciasDe(r).map(d => DISCREPANCIA_LABEL[d]).join(', '),
                header: 'Discrepâncias',
                size: 260,
                Cell: ({ row }) => (<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {discrepanciasDe(row.original).map((d) => (<Chip key={d} size='small' label={DISCREPANCIA_LABEL[d]} color={DISCREPANCIA_COR[d]}/>))}
          </Box>)
            }
        ];
    }, [faseVista]);
    const options = useMemo(() => ({
        filter: [
            { name: 'numeroProcesso', type: PaginationFilterType.TEXT },
            { name: 'numeroParceiro', type: PaginationFilterType.TEXT },
            { name: 'tipoInstalacao', type: PaginationFilterType.TEXT },
            { name: 'discrepancias', type: PaginationFilterType.TEXT }
        ],
        sort: ['numeroProcesso', 'numeroParceiro', 'data', 'tipoInstalacao', 'valorSemIva']
    }), []);
    const data = useMemo(() => {
        let rows = rec?.processos ?? [];
        if (soDiscrepancias)
            rows = rows.filter(r => discrepanciasDe(r).length > 0);
        const valor = (r, id) => id === 'discrepancias' ? discrepanciasDe(r).map(d => DISCREPANCIA_LABEL[d]).join(' ') : r[id];
        return paginar(filtrarOrdenar(rows, filter, sort, valor), pagination);
    }, [rec, faseVista, soDiscrepancias, filter, sort, pagination]);
    // ── Fase 3: resumo para faturar ───────────────────────────────────────────
    const paraFaturar = useMemo(() => (rec?.processos ?? []).filter(p => p.final), [rec]);
    const totalSelecionado = useMemo(() => Math.round(paraFaturar.filter(p => selecionados.has(p.id)).reduce((acc, p) => acc + p.totalProcesso, 0) * 100) / 100, [paraFaturar, selecionados]);
    /**
     * Marca ou desmarca um processo na fase 3.
     *
     * @param {number} id `gas_processo.id`
     */
    const alternar = (id) => setSelecionados(s => {
        const n = new Set(s);
        if (n.has(id))
            n.delete(id);
        else
            n.add(id);
        return n;
    });
    const faturado = !!rec?.periodo.faturado;
    // Tabela da fase 3: mesmo componente e mesmo comportamento da tabela de discrepâncias
    const [pagination3, setPagination3] = useState({ pageIndex: 0, pageSize: 20 });
    const [sort3, setSort3] = useState([]);
    const [filter3, setFilter3] = useState([]);
    const paraFaturarFiltrados = useMemo(() => filtrarOrdenar(paraFaturar, filter3, sort3, (r, id) => r[id]), [paraFaturar, filter3, sort3]);
    const data3 = useMemo(() => paginar(paraFaturarFiltrados, pagination3), [paraFaturarFiltrados, pagination3]);
    // Processos visíveis (com o filtro) que podem ser marcados e ainda não estão
    const porMarcar = paraFaturarFiltrados.filter(p => p.faturavel && !selecionados.has(p.id));
    /**
     * Marca todos os processos faturáveis visíveis (respeita o filtro).
     */
    const selecionarTodos = () => setSelecionados(s => {
        const n = new Set(s);
        porMarcar.forEach(p => n.add(p.id));
        return n;
    });
    const columns3 = useMemo(() => {
        return [
            {
                accessorKey: 'faturar',
                header: 'Faturar',
                size: 70,
                enableSorting: false,
                Cell: ({ row }) => (<Checkbox size='small' checked={selecionados.has(row.original.id)} disabled={!row.original.faturavel || faturado} onChange={() => alternar(row.original.id)}/>)
            },
            { accessorKey: 'numeroProcesso', header: 'Nº Processo', size: 110 },
            { accessorKey: 'numeroParceiro', header: 'Nº Parceiro', size: 120 },
            { accessorKey: 'data', header: 'Data Inspeção', size: 110, Cell: ({ cell }) => (cell.getValue() ? moment(cell.getValue()).format('DD/MM/YYYY') : '—') },
            { accessorKey: 'totalProcesso', header: 'Valor', Cell: ({ cell }) => money(cell.getValue()) },
            { accessorKey: 'tipoInstalacao', header: 'Tipo', size: 110 },
            { accessorKey: 'motivoNaoFaturavel', header: 'Observação', size: 220 }
        ];
    }, [selecionados, faturado]);
    const options3 = useMemo(() => ({
        filter: [
            { name: 'numeroProcesso', type: PaginationFilterType.TEXT },
            { name: 'numeroParceiro', type: PaginationFilterType.TEXT },
            { name: 'tipoInstalacao', type: PaginationFilterType.TEXT },
            { name: 'motivoNaoFaturavel', type: PaginationFilterType.TEXT }
        ],
        sort: ['numeroProcesso', 'numeroParceiro', 'data', 'tipoInstalacao', 'totalProcesso']
    }), []);
    /**
     * Cartão de uma fase: estado da carga e botão de carregar o ficheiro.
     *
     * @param {GasEdpFase} fase Fase do cartão
     * @param {string} descricao Que ficheiro se carrega nesta fase
     * @returns {JSX.Element} Cartão
     */
    const cartaoFase = (fase, descricao) => {
        const c = rec?.cargas[fase];
        return (<DynamicCard title={`Fase ${fase} — ${NOME_FASE[fase]}`} sx={{ height: '100%' }} loading={false} content={[
                { label: '', value: descricao },
                {
                    label: 'Carga:',
                    value: c ? `${c.ficheiroNome ?? '—'} · ${c.linhas} linhas · ${c.funcionario ?? '—'}, ${moment(c.dataRegisto).format('DD/MM/YYYY HH:mm')}` : 'Por carregar'
                },
                {
                    label: 'Não cruzadas:',
                    value: c
                        ? [
                            c.semProcesso ? `${c.semProcesso} sem processo no SGI` : '',
                            c.foraDoPeriodo ? `${c.foraDoPeriodo} fora do período ou não EDP` : '',
                            c.repetidos ? `${c.repetidos} repetidas` : ''
                        ]
                            .filter(Boolean)
                            .join(' · ') || '—'
                        : '—'
                },
                {
                    label: '',
                    value: (<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <input ref={inputs[fase]} type='file' style={{ display: 'none' }} accept='.csv,.xls,.xlsx' onChange={e => carregar(fase, e.target.files?.[0])}/>
                <Button size='small' variant='contained' disabled={loading || faturado} onClick={() => inputs[fase].current?.click()}>
                  {c ? 'Carregar novo ficheiro' : 'Carregar ficheiro'}
                </Button>
              </Box>)
                }
            ]}/>);
    };
    return (<Box>
      <Box sx={{ mt: 2, mb: 2 }}>
        <Breadcrumbs aria-label='breadcrumb'>
          <Link underline='none' color='inherit'>
            Gás
          </Link>
          <Link underline='hover' color='text.primary'>
            Reconciliação EDP
          </Link>
        </Breadcrumbs>
      </Box>

      {aviso && (<Alert severity={aviso.tipo} sx={{ mb: 2 }} onClose={() => setAviso(null)}>
          {aviso.texto}
        </Alert>)}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => setMes(m => m.clone().subtract(1, 'month'))} disabled={loading}>
          <ChevronLeft />
        </IconButton>
        <Typography variant='h6'>
          {moment(from).format('DD/MM/YYYY')} – {moment(to).format('DD/MM/YYYY')}
        </Typography>
        <IconButton onClick={() => setMes(m => m.clone().add(1, 'month'))} disabled={loading}>
          <ChevronRight />
        </IconButton>
        {faturado && (<Chip color='success' label={`Faturado${rec?.periodo.pedido?.documentoErp ? ` — ${rec.periodo.pedido.documentoErp}` : ''} (${ESTADO_PEDIDO[rec?.periodo.pedido?.estadoId ?? 1] ?? ''})`}/>)}
      </Box>

      <Grid container spacing={1}>
        <Grid xs={12} md={4}>
          {cartaoFase(1, 'CSV das tarefas fechadas no Click.')}
        </Grid>
        <Grid xs={12} md={4}>
          {cartaoFase(2, 'Ficheiro de resposta da EDP.')}
        </Grid>
        <Grid xs={12} md={4}>
          {cartaoFase(3, 'Ficheiro final validado pela EDP.')}
        </Grid>

        <Grid xs={12}>
          <DynamicCard title='Resumo' loading={loading} content={[
            { label: 'Processos EDP no período:', value: String(rec?.totais.count ?? 0) },
            { label: 'Com discrepâncias (fase 1 / fase 2):', value: `${rec?.totais.fase1 ?? 0} / ${rec?.totais.fase2 ?? 0}` },
            { label: 'Faturáveis:', value: `${rec?.totais.faturaveis ?? 0} · ${money(rec?.totais.totalFaturavel ?? 0)}` }
        ]}/>
        </Grid>

        <Grid xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <ToggleButtonGroup size='small' exclusive value={faseVista} onChange={(_, v) => v && setFaseVista(v)}>
              <ToggleButton value={1}>Fase 1 — Click</ToggleButton>
              <ToggleButton value={2}>Fase 2 — EDP</ToggleButton>
            </ToggleButtonGroup>
            <FormControlLabel control={<Switch checked={soDiscrepancias} onChange={e => setSoDiscrepancias(e.target.checked)}/>} label='Só discrepâncias'/>
          </Box>
          <Table 
    // Uma tabela por fase: as colunas Click/EDP mudam de id e o Table acrescentaria as novas no fim
    key={faseVista} storageKey={`${$state.current.name}.fase${faseVista}`} data={data} columns={columns} initial={{ sort, filter }} isLoading={loading} options={options} onPaginate={setPagination} onFilter={setFilter} onSort={setSort} onExport={rec?.cargas[faseVista] ? () => exportar(faseVista) : undefined} toolbarCustomActions={() => (<Button size='small' variant='outlined' disabled={!rec?.cargas[faseVista]} onClick={() => prepararEmail(faseVista)}>
                Preparar e-mail
              </Button>)}/>
        </Grid>

        {rec?.cargas[3] && (<Grid xs={12}>
            <DynamicCard title='Fase 3 — Processos a faturar' loading={false} variant='component' component={<Box>
                      {faturado && (<Alert severity='success' sx={{ mb: 1 }}>
                          Fatura pedida{rec.periodo.validadoPor ? ` por ${rec.periodo.validadoPor}` : ''}
                          {rec.periodo.validadoEm ? ` em ${moment(rec.periodo.validadoEm).format('DD/MM/YYYY HH:mm')}` : ''}.
                          {rec.periodo.pedido?.erroDescricao ? ` ${rec.periodo.pedido.erroDescricao}` : ''}
                        </Alert>)}
                      <Table storageKey={`${$state.current.name}.fase3.v2`} data={data3} columns={columns3} initial={{ sort: sort3, filter: filter3 }} isLoading={loading} options={options3} onPaginate={setPagination3} onFilter={setFilter3} onSort={setSort3} toolbarCustomActions={() => (<Button size='small' variant='outlined' disabled={faturado || porMarcar.length === 0} onClick={selecionarTodos}>
                            Selecionar todos
                          </Button>)}/>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mt: 1 }}>
                        <Typography>
                          {selecionados.size} processos · <b>{money(totalSelecionado)}</b>
                        </Typography>
                        <Button variant='contained' disabled={faturado || selecionados.size === 0 || loading} onClick={() => abrirConfirmacao()}>
                          Emitir fatura do período
                        </Button>
                      </Box>
                    </Box>}/>
          </Grid>)}
      </Grid>

      <Dialog open={confirmar} onClose={() => !emitindo && setConfirmar(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Emitir fatura do período</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Período de {moment(from).format('DD/MM/YYYY')} a {moment(to).format('DD/MM/YYYY')}.
          </Typography>
          <Typography gutterBottom>
            {selecionados.size} processos · <b>{money(totalSelecionado)}</b>
          </Typography>
          <Typography gutterBottom>
            Cliente: {config?.edpNome ?? '—'}
            {config?.edpNif ? ` (NIF ${config.edpNif})` : ''}
          </Typography>
          {config?.edpNumeroCompromisso && <Typography gutterBottom>Nº de compromisso: {config.edpNumeroCompromisso}</Typography>}
          <Typography variant='body2' color='text.secondary'>
            Depois de emitida, o período não aceita novas cargas nem outra fatura.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmar(false)} disabled={emitindo}>
            Cancelar
          </Button>
          <Button variant='contained' onClick={emitir} disabled={emitindo}>
            Emitir
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!email} onClose={() => setEmail(null)} maxWidth='md' fullWidth>
        <DialogTitle>Rascunho do e-mail à EDP</DialogTitle>
        <DialogContent>
          <TextField fullWidth size='small' label='Para' sx={{ mb: 2 }} value={email?.para ?? ''} onChange={e => email && setEmail({ ...email, para: e.target.value })}/>
          <TextField fullWidth size='small' label='Assunto' sx={{ mb: 2 }} value={email?.assunto ?? ''} onChange={e => email && setEmail({ ...email, assunto: e.target.value })}/>
          <TextField fullWidth multiline minRows={10} label='Texto' value={email?.corpo ?? ''} onChange={e => email && setEmail({ ...email, corpo: e.target.value })}/>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => email && exportar(email.fase)}>Descarregar anexo</Button>
          <Button onClick={() => {
            if (!email)
                return;
            navigator.clipboard?.writeText(email.corpo);
            UIService.addToast('Texto copiado.');
        }}>
            Copiar texto
          </Button>
          <Button variant='contained' onClick={() => {
            if (!email)
                return;
            window.location.href = `mailto:${encodeURIComponent(email.para)}?subject=${encodeURIComponent(email.assunto)}&body=${encodeURIComponent(email.corpo)}`;
        }}>
            Abrir no e-mail
          </Button>
          <Button onClick={() => setEmail(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>);
};
export default GasReconciliacaoEdpView;
